/** @jsxImportSource @emotion/react */

import { useLazyQuery, useQuery } from '@apollo/client'
import Typography from '@mui/material/Typography'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { findAllPlatforms, findAllPlatformsVariables } from '__generatedTypes__/findAllPlatforms'
import { findSubjectByPlatform, findSubjectByPlatformVariables } from '__generatedTypes__/findSubjectByPlatform'
import { GET_PLATFORMS } from 'queries/common'
import { FIND_SUBJECT_BY_PLATFORM } from 'queries/subject'
import { cmsRoutes, routes, useLink } from 'routes/cmsRoutes'
import { formatPlatformsWithKeys } from 'utils/formatPlatformsWithKeys'
import { PageLoader } from 'components/PageLoader'
import { useAuthContext } from 'utils/auth'
import { AI_TRAINER, MARKETING_GROUP } from 'utils/services/auth'
import { PlatformSlug, TAKE_ALL_ITEMS_COUNT } from 'constants/consts'

const FetchCoreContentLayout = ({ children }: { children: JSX.Element }) => {
  const { platform } = useParams()
  const { navigate } = useLink()
  const { groups } = useAuthContext()
  const [isInitialized, setIsInitialized] = useState(false)
  const [getSubjects, { data: subjectsData, loading: subjectsLoading }] = useLazyQuery<
    findSubjectByPlatform,
    findSubjectByPlatformVariables
  >(FIND_SUBJECT_BY_PLATFORM, {
    onCompleted: () => setIsInitialized(true),
    onError: () => setIsInitialized(true),
  })

  const { data: platformsData, loading: platformsLoading } = useQuery<findAllPlatforms, findAllPlatformsVariables>(
    GET_PLATFORMS,
    {
      variables: { query: { take: TAKE_ALL_ITEMS_COUNT } },
      onCompleted: (data) => {
        const platforms = formatPlatformsWithKeys(data?.findAllPlatforms.items || [])

        if (groups.includes(AI_TRAINER)) {
          const casperPlatform = platforms.find((p) => p.key === PlatformSlug.CasperBooster)
          if (!casperPlatform) {
            return
          }
          navigate(`/${casperPlatform.key}/${routes.TRAINING_DATA}`)
          return
        }

        const selectedPlatform = platforms?.find((p) => p.key === platform) || null

        if (
          groups.includes(MARKETING_GROUP) &&
          (!selectedPlatform || selectedPlatform.slug === PlatformSlug.Admissions)
        ) {
          navigate(`/${platforms[0].key}/${routes.USER_LIST}`)
          return
        }

        if (!selectedPlatform && platforms[0]) {
          navigate(`/${platforms[0].key}/${routes.CRASH_COURSE_LIST}`)
        }

        if (platform && !selectedPlatform) {
          navigate(cmsRoutes.NOT_FOUND)
        }
      },
    }
  )

  const selectedPlatform = useMemo(() => {
    const platforms = formatPlatformsWithKeys(platformsData?.findAllPlatforms.items || [])

    return platforms?.find((p) => p.key === platform) || null
  }, [platform, platformsData])

  useEffect(() => {
    if (selectedPlatform) {
      getSubjects({ variables: { platformId: selectedPlatform.id } })
    }
  }, [selectedPlatform, platform])

  if (!isInitialized || platformsLoading || subjectsLoading) {
    return <PageLoader />
  }

  if (!selectedPlatform || !subjectsData?.findSubjectByPlatform?.tabs) {
    //TODO Change error state
    return <Typography variant="h4">Error state</Typography>
  }

  return children
}

export default FetchCoreContentLayout
